import styled, { css } from 'styled-components'
import { InstagramLogo, TrolleyIcon, ChevronIcon } from '@sainsburys-tech/bolt-icons'
import { Button } from '@sainsburys-tech/bolt'
import { Icon, ImageContainer, IconButton } from './types'
import { visibleScrollBar } from '../../../helpers/StyledWrappers.styles'

export const StyledHeadingContainer = styled.header`
  display: flex;
`

/*  
  The image sizes vary from the designs in order to have some of the next image in the carousel peek through.
  This indicates to the user that there are more images to scroll through in the carousel.
  Tested on 58.33% of the width of Argos PDPs as this is where the component is currently used.
*/

export const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => props.theme.size.spacing.lg};
`

export const StyledImageContainer = styled.div<ImageContainer>`
  position: relative;
  flex-basis: min-content;
  min-width: 220px;

  ${(props) =>
    !props.isSainsburys &&
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) and (max-width: ${props.theme.size
          .breakpoint['max-width'].md}) {
        min-width: 155px;
      }

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].md}) and (max-width: ${props.theme.size
          .breakpoint['max-width'].md}) {
        min-width: 185px;
      }

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
        min-width: 205px;
      }
    `}

  img {
    width: 100%;
    cursor: pointer;
    ${(props) =>
      props.isSainsburys &&
      css`
        aspect-ratio: 1/1;
      `}
  }

  :hover {
    span {
      max-width: 160px;
    }

    label {
      display: inline;
    }
  }
`

const iconStyle = (isSainsburys) =>
  css`
    path {
      fill: ${(props) => (isSainsburys ? props.theme.color.monochrome.white : props.theme.color.monochrome.dark)};
    }
  `

export const StyledInstagramIcon = styled(InstagramLogo)<Icon>`
  ${(props) => iconStyle(props.isSainsburys)}
  width: 30px;
  height: 30px;
  margin-left: ${(props) => props.theme.size.spacing.md};

  ${(props) =>
    props.isSainsburys &&
    css`
      position: absolute;
      margin-left: 0;
      margin-right: ${props.theme.size.spacing.md};
      right: 0;
      bottom: 25%;
      cursor: pointer;
    `}
`

export const StyledTrolleyIcon = styled(TrolleyIcon)`
  ${iconStyle(false)}
  margin-right: ${(props) => props.theme.size.spacing.md};
`

export const StyledAuthorHandle = styled.p`
  position: unset;
  word-break: break-all;
  && {
    line-height: 1;
  }
`

export const StyledViewProductsIcon = styled.span<IconButton>`
  ${(props) => css`
    position: ${props.layout === 'slider' ? 'absolute' : 'relative'};
    bottom: ${props.layout === 'slider' ? '15%' : '3em'};
    margin-left: ${props.theme.size.spacing.sm};
    border-radius: ${props.theme.size.border.radius.xl};
    padding: ${props.theme.size.spacing.md};
  `}
  left: 0;
  background-color: rgba(255, 255, 255, 0.75);
  height: auto;
  white-space: nowrap;
  transition: max-width 0.5s, padding-right 0.45s;
  overflow: hidden;
  min-width: 50px;
  max-width: 60px;
  cursor: pointer;
`

export const StyledViewProductsLabel = styled.label`
  display: none;
  padding-left: ${(props) => props.theme.size.spacing.md};
  cursor: pointer;
`

export const StyledImagesWrapper = styled.div`
  display: flex;
  ${(props) =>
    css`
      gap: ${props.theme.size.spacing.lg};
      padding-bottom: ${props.theme.size.spacing.md};
    `}
  text-decoration: none;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  ${visibleScrollBar}
  min-height: 215px;
`

export const StyledButton = styled.button<ImageContainer>`
  all: unset;
  display: inline;
  width: 100%;
  height: 90%;

  :focus {
    outline: 5px auto -webkit-focus-ring-color;
  }

  ${(props) =>
    props.layout === 'stack' &&
    css`
    height: ${!props.isSainsburys && '100%'};
      img {
        aspect-ratio: 1/1;
        width: 100%;
        height: ${!props.isSainsburys && '100%'};
        cursor: pointer;
      }

      :hover {
        span {
          max-width: 160px;
        }

        label {
          display: inline;
        }

    `}
`

export const StyledCTALink = styled(Button)`
  height: 48px;
  margin-top: -${(props) => props.theme.size.spacing.md};
  padding: 0;
  :focus {
    outline: 5px auto -webkit-focus-ring-color;
    background-color: ${(props) => props.theme.color.palette['brand-1']};
  }
  ${(props) =>
    props.isCarousel &&
    css`
      position: absolute;
      bottom: 2%;
      :hover {
        background-color: ${props.theme.color.palette['brand-1']};
      }
    `};
`

export const StyledMiniButton = styled(Button)`
  width: fit-content;
  align-self: center;
  margin-top: ${(props) => props.theme.size.spacing.lg};
`

export const StyledLinkContainer = styled.div`
  align-self: center;
  flex: none;
`

export const StyledLink = styled.span`
  text-decoration: underline;
  font-weight: ${(props) => props.theme.font.weight.bold};
  cursor: pointer;
`

export const StyledChevron = styled(ChevronIcon)`
  ${(props) => css`
    margin-left: ${props.theme.size.spacing.sm};
    top: ${props.theme.size.spacing.sm};
  `}
  position: relative;
`

export const StyledTagsContainer = styled.div`
  overflow-y: hidden;
  overflow-x: auto;
  padding-bottom: ${(props) => props.theme.size.spacing.md};
  margin-bottom: ${(props) => props.theme.size.spacing.md};
  ul {
    margin-bottom: 0;
    margin-left: 0;
  }
`
